import React, { useMemo } from 'react';
import styled from 'styled-components';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import colors from '@honeybook/hbui/dist/styles/colors';
import { flexCenterAll } from '@honeybook/hbui/dist/styles/mixins';
import HBTooltip from '@honeybook/hbui/dist/components/HBTooltip/HBTooltip';
import ArrowDown16 from '@honeybook/icons/dist/icons/ArrowDown16';
import Pencil16 from '@honeybook/icons/dist/icons/Pencil16';
import { IOption } from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown.types';
import { observer } from 'mobx-react';
import { LabelSpan } from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown.styles';
import {
    AUTOMATION_FILE_TYPES,
    AUTOMATION_TYPES,
    IAutomationListItem
} from '../../../../../utils/automation.utils';
import { EDIT_AUTOMATION_TOOLTIP } from '../StepSideBar/StepSideBar.styles';
import { AutomationCombinedTypes } from '../../../AutomationsEnums';
import useAnalytics from '../../../../../common/useAnalytics';
import { AnalyticsEvents } from '../../../../../services/analytics/analytics-events';
import HBDropdown from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown';
import { useRootStore } from '~/stores/useRootStore';
import { useGlobalMX } from '~/providers/mx-global-context/useGlobalMX';

const ActionSelect = () => {
    const {
        automationUiStore: {
            isCreate,
            isSelectedStepEditingDisabled,
            tooltipCommonProps,
            setSelectedAutomationSidebarScreen,
            selectedStep,
            selectedStep: { combinedType, setStepAction }
        }
    } = useRootStore();
    const {
        userStore: { currUser }
    } = useGlobalMX();

    const userType = currUser.flowsBetaUserType;

    const menuItems = useMemo((): IOption[] => {
        const convertItemToOption = ({
            value,
            Icon,
            title
        }: IAutomationListItem): IOption => ({
            value,
            label: (
                <>
                    <Icon color={colors.neutral800} />
                    {title}
                </>
            )
        });
        const items = [AUTOMATION_TYPES.email, AUTOMATION_TYPES.task].map(
            convertItemToOption
        );
        if (userType.hasFlows) {
            items.push(convertItemToOption(AUTOMATION_TYPES.flow));
        }
        if (userType.hasFiles) {
            items.push(
                ...Object.values(AUTOMATION_FILE_TYPES).map(
                    ({ description: title, ...item }) =>
                        convertItemToOption({ ...item, title })
                )
            );
        }
        items.push(convertItemToOption(AUTOMATION_TYPES.pipeline));
        return items;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType]);

    const { track } = useAnalytics();

    const updateStepAction = (type: AutomationCombinedTypes) => {
        if (type === combinedType) {
            return;
        }
        track(AnalyticsEvents.AutomationsSideBarActionType, {
            action: 'click',
            type
        });
        if (type === 'brochure' || type === 'questionnaire') {
            setStepAction({ type: 'file', file_type: type });
        } else {
            setStepAction({ type });
        }
        setSelectedAutomationSidebarScreen(
            selectedStep.getSideBarConfig(userType).initialScreen
        );
    };

    return (
        <Container>
            <InputTitle type="Body6DarkPrimary">Action type</InputTitle>
            <DropDownContainer>
                <HBTooltip
                    {...tooltipCommonProps}
                    target={
                        <>
                            <HBDropdown
                                options={menuItems}
                                value={combinedType}
                                // @ts-ignore
                                onChange={updateStepAction}
                                size="sm"
                                disabled={isSelectedStepEditingDisabled}
                                noIndicator
                                testIdName="step-actions"
                            />
                            <DropDownIndicator>
                                {isCreate ? (
                                    <ArrowDown16 color={colors.neutral800} />
                                ) : (
                                    <Pencil16
                                        color={
                                            isSelectedStepEditingDisabled
                                                ? colors.neutral400
                                                : colors.neutral800
                                        }
                                    />
                                )}
                            </DropDownIndicator>
                        </>
                    }
                >
                    {EDIT_AUTOMATION_TOOLTIP}
                </HBTooltip>
            </DropDownContainer>
        </Container>
    );
};

export default observer(ActionSelect);

const Container = styled.div`
    padding: 12px 0;

    &:last-of-type {
        padding-bottom: 4px;
    }
`;

const InputTitle = styled(HBText)`
    display: block;
    padding-bottom: 8px;
`;

const DropDownContainer = styled.div`
    position: relative;
    ${LabelSpan} {
        max-width: none;
        display: flex;
        align-items: center;
        gap: 8px;
    }
`;

const DropDownIndicator = styled.div`
    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    ${flexCenterAll}
`;
